import axiosInstance from "core/api/axiosInstance";

// ============================================================
// DECODIFICAR TOKEN JWT
// ============================================================
const decodeToken = (token) => {
  try {
    if (!token) return null;
    const parts = token.split(".");
    if (parts.length !== 3) return null;

    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(decoded);
  } catch (error) {
    console.error("❌ Error al decodificar token:", error);
    return null;
  }
};

// ============================================================
// OBTENER USUARIO ACTUAL DEL LOCALSTORAGE
// ============================================================
const getCurrentUser = () => {
  try {
    const userData = localStorage.getItem("user");
    const token = localStorage.getItem("token");

    if (!userData) return null;

    const parsed = JSON.parse(userData);
    let userId = null;

    if (token) {
      const decoded = decodeToken(token);
      if (decoded && decoded.id) {
        userId = decoded.id;
      }
    }

    if (!userId && parsed.user?.id) {
      userId = parsed.user.id;
    }

    if (!userId && parsed.more?.id) {
      userId = parsed.more.id;
    }

    if (!userId && parsed.id) {
      if (parsed.role && parsed.id !== parsed.role.id) {
        userId = parsed.id;
      }
    }

    if (userId && parsed.role && userId === parsed.role.id) {
      userId = null;
      if (token) {
        const decoded = decodeToken(token);
        if (decoded && decoded.id) {
          userId = decoded.id;
        }
      }
    }

    let username = "";
    if (parsed.username) {
      username = parsed.username;
    } else if (parsed.user?.username) {
      username = parsed.user.username;
    } else if (parsed.user?.name) {
      username = parsed.user.name;
    } else if (parsed.name) {
      username = parsed.name;
    }

    let role = null;
    if (parsed.role) {
      role = parsed.role;
    } else if (parsed.user?.role) {
      role = parsed.user.role;
    }
    if (role && typeof role === "object") {
      role = role.name || role.id || null;
    }

    let commerceId = null;
    if (parsed.commerceId) {
      commerceId = parsed.commerceId;
    } else if (parsed.commerce_id) {
      commerceId = parsed.commerce_id;
    }

    let commerceName = null;
    if (parsed.commerceName) {
      commerceName = parsed.commerceName;
    } else if (parsed.commerce_name) {
      commerceName = parsed.commerce_name;
    }

    if (userId) {
      parsed.id = userId;
      localStorage.setItem("user", JSON.stringify(parsed));
    }

    return {
      id: userId,
      username: username,
      role: role,
      commerceId: commerceId ? Number(commerceId) : null,
      commerceName: commerceName,
      ...parsed,
    };
  } catch (error) {
    console.error("❌ Error al obtener usuario:", error);
    return null;
  }
};

// ============================================================
// OBTENER ID DEL USUARIO DESDE API
// ============================================================
const getUserIdFromAPI = async (username) => {
  try {
    const response = await axiosInstance.get("/api/users/all");
    if (response.data && Array.isArray(response.data)) {
      const user = response.data.find((u) => u.username === username);
      if (user) {
        return user.id;
      }
    }
    return null;
  } catch (error) {
    console.error("❌ Error al obtener usuarios desde API:", error);
    return null;
  }
};

// ============================================================
// OBTENER COMERCIOS DEL USUARIO
// ============================================================
export const getUserCommerces = async () => {
  try {
    let userData = getCurrentUser();
    if (!userData) {
      return { success: false, commerces: [], permisos: [], error: "Usuario no encontrado" };
    }

    let userId = userData.id;

    if (!userId) {
      const token = localStorage.getItem("token");
      if (token) {
        const decoded = decodeToken(token);
        if (decoded && decoded.id) {
          userId = decoded.id;
        }
      }
    }

    if (!userId && userData.username) {
      userId = await getUserIdFromAPI(userData.username);
      if (userId) {
        const rawData = localStorage.getItem("user");
        if (rawData) {
          const parsed = JSON.parse(rawData);
          parsed.id = userId;
          localStorage.setItem("user", JSON.stringify(parsed));
          userData = getCurrentUser();
        }
      }
    }

    if (!userId) {
      return {
        success: false,
        commerces: [],
        permisos: [],
        error: "No se encontró el ID del usuario",
      };
    }

    const rawData = localStorage.getItem("user");
    if (rawData) {
      const parsed = JSON.parse(rawData);
      if (!parsed.id || parsed.id === parsed.role?.id) {
        parsed.id = userId;
        localStorage.setItem("user", JSON.stringify(parsed));
      }
    }

    // Obtener permisos
    const response = await axiosInstance.get("/api/automatization/cbrs/co_prop/roles");

    if (response.data && response.data.msm === "succesful") {
      const permisos = response.data.data || [];

      const userPermisos = permisos.filter((p) => p.co_user === Number(userId));

      const propiedadIds = [...new Set(userPermisos.map((p) => p.co_prop_id))];

      if (propiedadIds.length === 0) {
        const updatedUserData = { ...userData, id: userId, permissions: [], commerces: [] };
        localStorage.setItem("user", JSON.stringify(updatedUserData));
        return {
          success: false,
          commerces: [],
          permisos: [],
          error: "No tiene permisos para ninguna propiedad",
        };
      }

      const commerceResponse = await axiosInstance.get("/api/automatization/cbrs/co_prop/commerce");

      if (commerceResponse.data && commerceResponse.data.msm === "succesful") {
        const allCommerces = commerceResponse.data.data || [];
        const userCommerces = allCommerces.filter((c) => propiedadIds.includes(c.id));

        // 🔥 GUARDAR PERMISOS ACTUALIZADOS EN LOCALSTORAGE
        const updatedUserData = {
          ...userData,
          id: userId,
          permissions: userPermisos, // 🔥 Esto es clave
          commerces: userCommerces,
          commerceId:
            userCommerces.length === 1 ? userCommerces[0].id : userData?.commerceId || null,
          commerceName:
            userCommerces.length === 1 ? userCommerces[0].name : userData?.commerceName || null,
        };
        localStorage.setItem("user", JSON.stringify(updatedUserData));

        return { success: true, commerces: userCommerces, permisos: userPermisos };
      }
    }

    return { success: false, commerces: [], permisos: [], error: "No se encontraron comercios" };
  } catch (error) {
    console.error("❌ Error al obtener comercios:", error);
    return { success: false, commerces: [], permisos: [], error: error.message };
  }
};

// ============================================================
// INICIALIZAR COMMERCE ID
// ============================================================
export const initializeUserCommerce = async () => {
  try {
    const userData = getCurrentUser() || {};

    if (userData.commerceId) {
      const commerces = userData.commerces || [];
      const exists = commerces.some((c) => c.id === Number(userData.commerceId));
      if (exists) {
        return { success: true, commerceId: Number(userData.commerceId) };
      }
    }

    const result = await getUserCommerces();

    if (result.success && result.commerces.length > 0) {
      const selectedCommerce = result.commerces[0];
      const updatedUserData = {
        ...userData,
        commerceId: Number(selectedCommerce.id),
        commerceName: selectedCommerce.name,
        commerces: result.commerces,
        permissions: result.permisos || [],
      };
      localStorage.setItem("user", JSON.stringify(updatedUserData));

      return {
        success: true,
        commerceId: Number(selectedCommerce.id),
        commerceName: selectedCommerce.name,
        commerces: result.commerces,
        permissions: result.permisos || [],
      };
    }

    return { success: false, error: "No hay comercios disponibles" };
  } catch (error) {
    console.error("❌ Error al inicializar comercio:", error);
    return { success: false, error: error.message };
  }
};

// ============================================================
// CAMBIAR COMERCIO ACTIVO
// ============================================================
export const switchCommerce = (commerceId, commerceName) => {
  try {
    const userData = getCurrentUser() || {};
    const updatedUserData = {
      ...userData,
      commerceId: Number(commerceId),
      commerceName: commerceName,
    };
    localStorage.setItem("user", JSON.stringify(updatedUserData));
    return { success: true };
  } catch (error) {
    console.error("❌ Error al cambiar comercio:", error);
    return { success: false, error: error.message };
  }
};

// ============================================================
// OBTENER COMMERCE ID / NAME
// ============================================================
export const getCommerceId = () => {
  try {
    const userData = getCurrentUser() || {};
    return userData.commerceId ? Number(userData.commerceId) : null;
  } catch {
    return null;
  }
};

export const getCommerceName = () => {
  try {
    const userData = getCurrentUser() || {};
    return userData.commerceName || null;
  } catch {
    return null;
  }
};

// ============================================================
// VERIFICAR PERMISOS POR MÓDULO
// ============================================================
export const hasModulePermission = (moduleName, action = "ver") => {
  try {
    const userData = getCurrentUser() || {};
    const permissions = userData.permissions || [];

    // 🔥 Buscar CUALQUIER permiso activo para el módulo (sin importar la propiedad)
    const hasPermission = permissions.some((p) => {
      const matchesTable = p.co_table === moduleName;
      const matchesAction = p.rol?.[action] === true;
      return matchesTable && matchesAction;
    });

    return hasPermission;
  } catch (error) {
    console.error("❌ Error al verificar permiso:", error);
    return false;
  }
};

// ============================================================
// OBTENER PERMISOS POR MÓDULO
// ============================================================
export const getUserPermissionsForModule = (moduleName) => {
  try {
    const userData = getCurrentUser() || {};
    const permissions = userData.permissions || [];
    return permissions.filter((p) => p.co_table === moduleName);
  } catch {
    return [];
  }
};

// ============================================================
// OBTENER COMERCIOS DISPONIBLES (para el selector)
// ============================================================
export const getAvailableCommerces = () => {
  try {
    const userData = getCurrentUser() || {};
    return userData.commerces || [];
  } catch {
    return [];
  }
};

// ============================================================
// EXPORTAR getCurrentUser PARA COMPATIBILIDAD
// ============================================================
export { getCurrentUser };
